<template>
    <div>
        <div
            v-if="!loading && !isAdoptSegment"
            class="segment-rules-list">
            <h4
                v-if="showTitle && (parsedRules.length > 0 || isEveryone)"
                class="segment-rules--label">
                {{ $t('segmentRulesList.Rules summary') }}
            </h4>
            <div
                v-if="isEveryone"
                class="segment-rules-list-rule">
                {{ $t('segmentRulesList.Everyone in your app will see this.') }}
            </div>
            <segment-rule-description
                v-if="identifiedLabel && showIdentifiedLabel"
                :rule="{ text: identifiedLabel }"
                icon-type="visitor" />
            <div
                v-if="!isEveryone"
                class="segment-rules-items">
                <div
                    v-for="(rule, index) in parsedRules"
                    :key="index"
                    class="segment-rules-list-rule">
                    <div
                        v-if="index > 0"
                        class="segment-editor__rule-seperator">
                        {{ $t('segmentRulesList.AND') }}
                        <pendo-divider
                            class="rule-and-separator"
                            height="44px"
                            decorative />
                    </div>
                    <div v-if="rule.or">
                        <div
                            v-for="(ruleOr, j) in rule.or"
                            :key="j"
                            class="segment-rules-list-rule">
                            <div
                                v-if="j > 0"
                                class="rule-or-separator">
                                {{ $t('segmentRulesList.OR') }}
                            </div>
                            <segment-rule-description
                                :rule="ruleOr"
                                :icon-type="getIcon(rules[index].or[j])"
                                :uses-event-property="rules[index].or[j].eventPropertyName !== undefined"
                                :open-links-in-new-tab="openLinksInNewTab"
                                @link-click="$emit('link-click', $event)" />
                        </div>
                    </div>
                    <segment-rule-description
                        v-else
                        :rule="rule"
                        :icon-type="getIcon(rules[index])"
                        :uses-event-property="rules[index].eventPropertyName !== undefined"
                        :open-links-in-new-tab="openLinksInNewTab"
                        @link-click="$emit('link-click', $event)" />
                </div>
            </div>
        </div>
        <div
            v-if="loading"
            class="segment-rules-loading">
            <pendo-loading-indicator
                v-for="n in ruleCount || rules.length"
                :key="n"
                type="skeleton"
                :skeleton-props="{ height: 20, width: 150 }" />
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { PendoDivider, PendoLoadingIndicator } from '@pendo/components';
import SegmentRuleDescription from './segment-rule-description.vue';
import { getFilterSummary } from '@/segments/summary.js';
import { findIdentified } from '@pendo/agg-connect';
import get from 'lodash/get';
import isFunction from 'lodash/isFunction';
import matches from 'lodash/matches';
import { MOBILE_VERSION_RULES_LIST, isAdoptSegment } from '@utils/segments';
import { SESSION_PROPERTY_NAMES, UTM_PARAMETER_NAMES, AUTOMATIC_GROUP_NAMES } from '@/constants/utm-parameters';

export default {
    name: 'SegmentRulesList',
    components: {
        PendoDivider,
        PendoLoadingIndicator,
        SegmentRuleDescription,
    },
    props: {
        rules: {
            type: [Array, String],
            required: true,
        },
        ruleCount: {
            type: Number,
            default: 1,
        },
        segment: {
            type: Object,
            required: true,
        },
        showTitle: {
            type: Boolean,
            default: true,
        },
        guideAudience: {
            type: Array,
            default: undefined,
        },
        openLinksInNewTab: {
            type: Boolean,
            default: false,
        },
        showGuidesToAnon: {
            type: Boolean,
        },
        showAnonymousVisitorData: {
            type: Boolean,
        },
        showIdentifiedLabel: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['link-click', 'rules-parsed'],
    data () {
        return {
            loading: false,
            parsedRules: [],
            // one is filtering to only anonyomus visitors and the other to only identified visitors -- resulting in no visitors
            hasConflictingProps: matches(['!visitorId', 'visitorId']), // need to sort array passed in
            // if the associated flag is disabled then anonymous visitors will be excluded,
            // so filtering down to only anons would result in no visitors
            hasConflictingFlagAndProp: matches([false, '!visitorId']),
        };
    },
    computed: {
        ...mapGetters({
            everyPossibleRuleDependency: 'segments/everyPossibleRuleDependency',
        }),
        isAdoptSegment () {
            return isAdoptSegment(this.segment);
        },
        isEveryone () {
            return this.segment && (this.segment.id === 'everyone' || this.rules === 'everyone');
        },
        identifiedLabel () {
            const { guideAudience, showGuidesToAnon, showAnonymousVisitorData } = this;
            const hasGuideAudience = !!guideAudience;
            const pipeline = get(this.segment, 'pipeline');
            const segmentPipeline = isFunction(pipeline) ? pipeline() : pipeline;
            const isNestedSegment = !!get(this.segment, 'compound');

            const hasSegmentPipeline = !!segmentPipeline;
            const guideIdentifiedProp = get(findIdentified(guideAudience), 'identified');
            const segmentIdentifiedProp = get(findIdentified(segmentPipeline), 'identified');

            // key pointers -- inclusion of anon visitors in `visitors` source driven
            // by the `showGuidesToAnon` flag in guide audiences and `showAnonymousVisitorData` flag in app analytics.
            // also, when `showAnonymousVisitorData` is disabled the `identified` operator is always
            // used in conjunction with a segment operator so the existence of an identified operator
            // inside of the segment becomes irrelevant unless they are conflicting.
            if (hasGuideAudience) {
                if (
                    this.hasConflictingProps([guideIdentifiedProp, segmentIdentifiedProp].sort()) ||
                    this.hasConflictingFlagAndProp([showGuidesToAnon, guideIdentifiedProp]) ||
                    this.hasConflictingFlagAndProp([showGuidesToAnon, segmentIdentifiedProp])
                ) {
                    return this.$t('segmentRulesList.identifiedLabel.No Visitors');
                    // if we fall through the top `if` then `guideIdentifiedProp` and `segmentIdentifiedProp`
                    // are one of {'visitorId', '!visitorId'} or `undefined`, so not conflicting
                }
                if (
                    showGuidesToAnon === false ||
                    guideIdentifiedProp === 'visitorId' ||
                    segmentIdentifiedProp === 'visitorId'
                ) {
                    return this.$t('segmentRulesList.identifiedLabel.Identified Visitors Only');
                }
                if (guideIdentifiedProp === '!visitorId' || segmentIdentifiedProp === '!visitorId') {
                    return this.$t('segmentRulesList.identifiedLabel.Anonymous Visitors Only');
                }
                if (!guideIdentifiedProp && !segmentIdentifiedProp) {
                    return this.$t('segmentRulesList.identifiedLabel.Identified & Anonymous Visitors');
                }
                // note the separation here--if `hasGuideAudience` is false then we are dealing
                // with a plain ol' segment, meaning we need to look at the `showAnonymousVisitorData` flag instead
            } else if (hasSegmentPipeline) {
                if (this.hasConflictingFlagAndProp([showAnonymousVisitorData, segmentIdentifiedProp])) {
                    return this.$t('segmentRulesList.identifiedLabel.No Visitors');
                }
                if (showAnonymousVisitorData === false || segmentIdentifiedProp === 'visitorId') {
                    return this.$t('segmentRulesList.identifiedLabel.Identified Visitors Only');
                }
                if (segmentIdentifiedProp === '!visitorId') {
                    return this.$t('segmentRulesList.identifiedLabel.Anonymous Visitors Only');
                }
                if (!segmentIdentifiedProp) {
                    return this.$t('segmentRulesList.identifiedLabel.Identified & Anonymous Visitors');
                }
            } else if (isNestedSegment) {
                if (showAnonymousVisitorData === false) {
                    return this.$t('segmentRulesList.identifiedLabel.Identified Visitors Only');
                }

                return this.$t('segmentRulesList.identifiedLabel.Identified & Anonymous Visitors');
            }

            return '';
        },
    },
    watch: {
        rules () {
            this.updateParsedSegmentRules();
        },
    },
    async created () {
        this.loading = true;
        await this.loadEveryPossibleRuleDependency();
        this.updateParsedSegmentRules();
        this.loading = false;
    },
    methods: {
        ...mapActions({
            loadEveryPossibleRuleDependency: 'segments/loadEveryPossibleRuleDependency',
        }),
        getIcon (rule) {
            if (MOBILE_VERSION_RULES_LIST.includes(rule.id)) {
                return 'smartphone';
            }

            if (UTM_PARAMETER_NAMES.includes(rule.name)) {
                return 'utm';
            }

            if (AUTOMATIC_GROUP_NAMES.includes(rule.name)) {
                return 'session-properties';
            }

            const { type } = rule;
            switch (type?.split('.')[0]) {
                case 'feature':
                    return 'feature';
                case 'visitor':
                case 'useragent':
                    return 'user';
                case 'account':
                    return 'users';
                case 'parentAccount':
                    return 'home';
                case 'guide':
                case 'resourceCenter':
                    return 'guide';
                case 'guideElement':
                    return 'guideElement';
                case 'page':
                    return 'file';
                case 'trackType':
                    return 'zap';
                case 'mobile':
                    return 'smartphone';
                case 'poll':
                    return 'list';
                case 'feedbackRequest':
                case 'idea':
                    return 'lightbulb';
                case 'survey':
                    return 'surveys';
                case 'segment':
                    return 'pie-chart';
                case 'journey':
                    return 'orchestrate';
                case 'email':
                    return 'email';
                case 'guideExperiment':
                    return 'beaker';
                default:
                    return undefined;
            }
        },
        updateParsedSegmentRules () {
            if (this.isEveryone) {
                this.$emit('rules-parsed', []);

                return;
            }
            if (this.segment?.createdByApi || this.segment?.definition?.apiManagedList) {
                this.parsedRules = [
                    {
                        text: this.$t(
                            'segmentRulesList.updateParsedSegmentRules.Visitors contained within an API-managed list',
                        ),
                    },
                ];
                this.$emit('rules-parsed', this.parsedRules);

                return;
            }

            this.loading = true;
            const rules = getFilterSummary(this.rules, this.everyPossibleRuleDependency);
            this.loading = false;
            this.parsedRules = rules;
            this.removeRuleTypeFromParsedRules();
            this.$emit('rules-parsed', this.parsedRules);
        },
        removeRuleTypeFromParsedRules () {
            this.parsedRules.forEach((rule, index) => {
                if (rule.or) {
                    rule.or.forEach((ruleOr, orIndex) => {
                        this.parsedRules[index].or[orIndex].text = this.splitAndJoinText(ruleOr);
                    });
                } else {
                    this.parsedRules[index].text = this.splitAndJoinText(rule);
                }
            });
        },
        splitAndJoinText (oneRule) {
            // No need to manually remove type
            // for localized text
            if (oneRule.isLocalized) {
                return oneRule.text;
            }

            const words = oneRule.text.split(' ');
            const [type, ...otherWords] = words;
            let textWithoutType;

            switch (type) {
                case 'Response':
                case 'Number':
                case 'Time':
                case 'Segment':
                    textWithoutType = oneRule.text;
                    break;
                case 'Track':
                    otherWords.shift();
                    textWithoutType = otherWords.join(' ');
                    break;
                default:
                    textWithoutType = otherWords.join(' ');
                    break;
            }

            if (SESSION_PROPERTY_NAMES.includes(type)) {
                textWithoutType = oneRule.text;
            }

            return textWithoutType;
        },
    },
};
</script>

<style scoped lang="less">
.segment-rules--label {
    color: var(--palette-gray-110);
    padding-bottom: 8px;
    font-weight: 700;
    margin: 0;
    font-size: 14px;
}

.segment-rules-list-rule {
    color: var(--palette-gray-70);
    line-height: 21px;

    .rule-or-separator {
        color: var(--palette-gray-70);
        line-height: 30px;
    }
}

.segment-rules-items {
    overflow: auto;
}

.segment-rules-loading :deep(svg) {
    margin-bottom: 20px;
}

.segment-editor__rule-seperator {
    display: flex;
    align-items: center;
    color: var(--palette-gray-70);

    .pendo-divider {
        margin-left: 8px;
    }
}
</style>
